import { useRef, useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Instagram, Loader2, X, Eye, EyeOff } from "lucide-react";
import { getFontEmbedCSS, toBlob, toPng } from "html-to-image";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InstagramPost1 } from "./instagram/InstagramPost1";
import { InstagramPost1Colorful } from "./instagram/InstagramPost1Colorful";
import { InstagramPost1Emoji } from "./instagram/InstagramPost1Emoji";
import { InstagramPost2 } from "./instagram/InstagramPost2";
import { InstagramPost2Colorful } from "./instagram/InstagramPost2Colorful";
import { InstagramPost2Bento } from "./instagram/InstagramPost2Bento";
import { InstagramPost2BentoV2 } from "./instagram/InstagramPost2BentoV2";
import { InstagramPost2Minimal } from "./instagram/InstagramPost2Minimal";
import { InstagramPost2Stats } from "./instagram/InstagramPost2Stats";
import { InstagramPost2Bubbles } from "./instagram/InstagramPost2Bubbles";
import { InstagramPost3 } from "./instagram/InstagramPost3";
import { InstagramPost3Timeline } from "./instagram/InstagramPost3Timeline";
import { InstagramPost3Cards } from "./instagram/InstagramPost3Cards";
import { InstagramPost3Circles } from "./instagram/InstagramPost3Circles";
import { InstagramStory } from "./instagram/InstagramStory";
import { InstagramStoryVerified1 } from "./instagram/InstagramStoryVerified1";
import { InstagramStoryVerified2 } from "./instagram/InstagramStoryVerified2";
import { InstagramStoryVerified3 } from "./instagram/InstagramStoryVerified3";
import { InstagramStory12Verified1 } from "./instagram/InstagramStory12Verified1";
import { InstagramStory12Verified2 } from "./instagram/InstagramStory12Verified2";
import { InstagramStory12Verified3 } from "./instagram/InstagramStory12Verified3";
import { InstagramStory12Gradient } from "./instagram/InstagramStory12Gradient";
import { InstagramStory12Polaroid } from "./instagram/InstagramStory12Polaroid";
import { InstagramStory12Minimal } from "./instagram/InstagramStory12Minimal";
import { InstagramStory12Clean } from "./instagram/InstagramStory12Clean";
import { InstagramStoryShare1 } from "./instagram/InstagramStoryShare1";
import { InstagramStoryShare2 } from "./instagram/InstagramStoryShare2";
import { InstagramStoryShare3 } from "./instagram/InstagramStoryShare3";
import { InstagramStoryShare4 } from "./instagram/InstagramStoryShare4";
import { InstagramStoryShare5 } from "./instagram/InstagramStoryShare5";
import { InstagramStoryShare6 } from "./instagram/InstagramStoryShare6";
import { InstagramStoryShare7 } from "./instagram/InstagramStoryShare7";
import { InstagramStoryShare8 } from "./instagram/InstagramStoryShare8";
import { InstagramStoryShare9 } from "./instagram/InstagramStoryShare9";
import { InstagramStoryShare10 } from "./instagram/InstagramStoryShare10";
import { InstagramStoryShare11 } from "./instagram/InstagramStoryShare11";
import { InstagramStoryShare12 } from "./instagram/InstagramStoryShare12";
import { InstagramStoryShare13 } from "./instagram/InstagramStoryShare13";
import { InstagramStoryShare13A } from "./instagram/InstagramStoryShare13A";
import { InstagramStoryShare13B } from "./instagram/InstagramStoryShare13B";
import { InstagramStoryShare13C } from "./instagram/InstagramStoryShare13C";
import { InstagramStory4Verified1 } from "./instagram/InstagramStory4Verified1";
import { InstagramStory4Verified2 } from "./instagram/InstagramStory4Verified2";
import { InstagramStory4Verified3 } from "./instagram/InstagramStory4Verified3";
import { FacebookCover } from "./instagram/FacebookCover";
import { HighlightVerified } from "./instagram/HighlightVerified";
import shareCardBg from "@/assets/share-card-bg.png";
import { imageToDataUrl } from "@/hooks/useImageDataUrl";
import { dataUrlToPngBlobChunked, waitForImages } from "./instagram/exportUtils";
import { composePngOnBackground } from "@/lib/export/compositePng";

interface GraphicConfig {
  id: string;
  label: string;
  format: string;
  width: number;
  height: number;
  Component: React.ComponentType<{ backgroundDataUrl?: string }>;
}

const graphics: GraphicConfig[] = [
  { id: "story-share-13c", label: "Story: Witaj (emoji karty)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare13C },
  { id: "story-share-13b", label: "Story: Witaj (zielone kółka)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare13B },
  { id: "story-share-13a", label: "Story: Witaj (ikony + opisy)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare13A },
  { id: "story-share-13", label: "Story: Witaj w społeczności (3 kroki)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare13 },
  { id: "story-share-12", label: "Story: Udostępnij (checklist)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare12 },
  { id: "story-share-11", label: "Story: Udostępnij (społeczność zwierząt)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare11 },
  { id: "story-share-10", label: "Story: Udostępnij (wyślij 3 osobom)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare10 },
  { id: "story-share-9", label: "Story: Udostępnij (efekt domina)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare9 },
  { id: "story-share-8", label: "Story: Udostępnij (wyślij znajomemu)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare8 },
  { id: "story-share-7", label: "Story: Udostępnij (społeczność)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare7 },
  { id: "highlight-verified", label: "Highlight: Zweryfikowane", format: "1080×1080", width: 1080, height: 1080, Component: HighlightVerified },
  { id: "fb-cover", label: "Facebook: Okładka", format: "1640×624", width: 1640, height: 624, Component: FacebookCover },
  { id: "story-share-6", label: "Story: Udostępnij (statystyki)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare6 },
  { id: "story-share-5", label: "Story: Udostępnij (pytanie)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare5 },
  { id: "story-share-4", label: "Story: Udostępnij (łańcuch)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare4 },
  { id: "story-share-3", label: "Story: Udostępnij (emoji)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare3 },
  { id: "story-share-2", label: "Story: Udostępnij (karty)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare2 },
  { id: "story-share-1", label: "Story: Udostępnij (minimal)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryShare1 },
  { id: "story-4-verified-3", label: "Story: 4 Nowe weryfikacje (siatka 2×2)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory4Verified3 },
  { id: "story-4-verified-2", label: "Story: 4 Nowe weryfikacje (numerowane)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory4Verified2 },
  { id: "story-4-verified-1", label: "Story: 4 Nowe weryfikacje (karty)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory4Verified1 },
  { id: "story-12-clean", label: "Story: 12 Zweryfikowanych (czysty)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory12Clean },
  { id: "story-12-minimal", label: "Story: 12 Zweryfikowanych (minimal)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory12Minimal },
  { id: "story-12-polaroid", label: "Story: 12 Zweryfikowanych (polaroid)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory12Polaroid },
  { id: "story-12-gradient", label: "Story: 12 Zweryfikowanych (gradient)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory12Gradient },
  { id: "story-12-verified-3", label: "Story: 12 Zweryfikowanych (lista)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory12Verified3 },
  { id: "story-12-verified-2", label: "Story: 12 Zweryfikowanych (badge'e)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory12Verified2 },
  { id: "story-12-verified-1", label: "Story: 12 Zweryfikowanych (siatka)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory12Verified1 },
  { id: "story-verified-3", label: "Story: 8 Zweryfikowanych (timeline)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryVerified3 },
  { id: "story-verified-2", label: "Story: 8 Zweryfikowanych (siatka)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryVerified2 },
  { id: "story-verified-1", label: "Story: 8 Zweryfikowanych (lista)", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStoryVerified1 },
  { id: "story", label: "Story: Wprowadzenie", format: "1080×1920", width: 1080, height: 1920, Component: InstagramStory },
  { id: "post-3-circles", label: "Post 3: Kółka", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost3Circles },
  { id: "post-3-cards", label: "Post 3: Karty", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost3Cards },
  { id: "post-3-timeline", label: "Post 3: Timeline", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost3Timeline },
  { id: "post-3", label: "Post 3: Jak pomóc", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost3 },
  { id: "post-2-bubbles", label: "Post 2: Czat Q&A", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost2Bubbles },
  { id: "post-2-stats", label: "Post 2: Statystyki", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost2Stats },
  { id: "post-2-minimal", label: "Post 2: Lista z emoji", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost2Minimal },
  { id: "post-2-bento-v2", label: "Post 2: Bento Grid (zielony)", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost2BentoV2 },
  { id: "post-2-bento", label: "Post 2: Bento Grid", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost2Bento },
  { id: "post-2-color", label: "Post 2: Funkcje (kolorowy)", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost2Colorful },
  { id: "post-2", label: "Post 2: Funkcje", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost2 },
  { id: "post-1-emoji", label: "Post 1: Wprowadzenie (z emoji)", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost1Emoji },
  { id: "post-1-icons", label: "Post 1: Wprowadzenie (z ikonkami)", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost1Colorful },
  { id: "post-1", label: "Post 1: Wprowadzenie", format: "1080×1080", width: 1080, height: 1080, Component: InstagramPost1 },
];

const HIDDEN_GRAPHICS_KEY = "hidden-instagram-graphics";

const getHiddenGraphics = (): string[] => {
  try {
    const stored = localStorage.getItem(HIDDEN_GRAPHICS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const setHiddenGraphics = (ids: string[]) => {
  localStorage.setItem(HIDDEN_GRAPHICS_KEY, JSON.stringify(ids));
};

export const InstagramGraphicsTab = () => {
  const exportRef = useRef<HTMLDivElement | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [bgDataUrl, setBgDataUrl] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<string[]>(() => getHiddenGraphics());
  const [showHidden, setShowHidden] = useState(false);
  const [iosSave, setIosSave] = useState<null | { filename: string; file: File; blobUrl: string; shareSupported: boolean }>(null);

  const exportFallbackBackgroundColor = (() => {
    // If something goes wrong and background image isn't captured,
    // don't produce a transparent PNG (which shows as black in Photos on iOS).
    try {
      const bg = getComputedStyle(document.documentElement)
        .getPropertyValue("--background")
        .trim();
      return bg ? `hsl(${bg})` : "white";
    } catch {
      return "white";
    }
  })();

  const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Pre-convert background image to data URL on mount
  useEffect(() => {
    imageToDataUrl(shareCardBg).then(setBgDataUrl);
  }, []);

  const getBackgroundForGraphic = (_id: string) => {
    return bgDataUrl || undefined;
  };

  const hideGraphic = (id: string) => {
    const newHidden = [...hiddenIds, id];
    setHiddenIds(newHidden);
    setHiddenGraphics(newHidden);
    toast.success("Ukryto szablon");
  };

  const restoreGraphic = (id: string) => {
    const newHidden = hiddenIds.filter((h) => h !== id);
    setHiddenIds(newHidden);
    setHiddenGraphics(newHidden);
    toast.success("Przywrócono szablon");
  };

  const restoreAll = () => {
    setHiddenIds([]);
    setHiddenGraphics([]);
    toast.success("Przywrócono wszystkie szablony");
  };

  const visibleGraphics = graphics.filter((g) => !hiddenIds.includes(g.id));
  const hiddenGraphics = graphics.filter((g) => hiddenIds.includes(g.id));

  const exportingGraphic = downloading ? graphics.find((g) => g.id === downloading) : undefined;

  const nextPaint = async () => {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    // iOS sometimes needs an extra frame for images/layout to settle
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  };

  const withDisabledCrossOriginStylesheets = async <T,>(fn: () => Promise<T>) => {
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')
    );

    const crossOriginLinks = links.filter((l) => {
      const href = l.getAttribute("href") || "";
      if (!href) return false;
      // data/blob are safe; relative urls are same-origin
      if (href.startsWith("data:") || href.startsWith("blob:") || href.startsWith("/")) return false;
      try {
        return new URL(href, window.location.href).origin !== window.location.origin;
      } catch {
        return false;
      }
    });

    const prev = crossOriginLinks.map((l) => l.disabled);
    crossOriginLinks.forEach((l) => (l.disabled = true));
    try {
      return await fn();
    } finally {
      crossOriginLinks.forEach((l, i) => (l.disabled = prev[i]));
    }
  };

  const downloadGraphic = async (id: string, filename: string, width: number, height: number) => {
    const config = graphics.find((g) => g.id === id);
    if (!config) return;

    setDownloading(id);
    let restoreRootBg: (() => void) | null = null;
    try {
      // Wait for the export stage to mount and stabilize
      await nextPaint();
      await new Promise((resolve) => setTimeout(resolve, isMobileDevice ? 300 : 100));
      await nextPaint();

      const element = exportRef.current;
      if (!element) throw new Error("Export stage not ready");

      // iOS: the graphic root often has a fallback solid background (green) which can cover
      // the composed background. Force it transparent during the foreground capture.
      if (isMobileDevice) {
        const graphicRoot = element.firstElementChild as HTMLElement | null;
        if (graphicRoot) {
          const prevValue = graphicRoot.style.getPropertyValue("background-color");
          const prevPriority = graphicRoot.style.getPropertyPriority("background-color");
          graphicRoot.style.setProperty("background-color", "transparent", "important");
          restoreRootBg = () => {
            if (prevValue) graphicRoot.style.setProperty("background-color", prevValue, prevPriority);
            else graphicRoot.style.removeProperty("background-color");
          };
        }
      }

      // Ensure Nunito weights are loaded before rendering to canvas
      if (document.fonts?.load) {
        await Promise.all([
          document.fonts.load('400 16px "Nunito"'),
          document.fonts.load('500 16px "Nunito"'),
          document.fonts.load('600 16px "Nunito"'),
          document.fonts.load('700 16px "Nunito"'),
          document.fonts.load('800 16px "Nunito"'),
        ]);
      }
      await document.fonts.ready;
      
      // Longer timeout on mobile for fonts + layout to stabilize
      await new Promise((resolve) => setTimeout(resolve, isMobileDevice ? 500 : 150));

      // Wait for all images to load
      await waitForImages(element);
      await nextPaint();

      // Embed local @font-face rules
      let fontEmbedCSS = "";
      try {
        fontEmbedCSS = await withDisabledCrossOriginStylesheets(async () => {
          return await getFontEmbedCSS(element);
        });
        console.info("[export] fontEmbedCSS length:", fontEmbedCSS?.length ?? 0);
      } catch (e) {
        console.warn("[export] getFontEmbedCSS failed:", e);
        fontEmbedCSS = "";
      }

      const baseOptions = {
        cacheBust: true,
        pixelRatio: isMobileDevice ? 1 : 2,
        width,
        height,
        skipFonts: false,
        fontEmbedCSS,
        backgroundColor: exportFallbackBackgroundColor,
      } as const;

      // Mobile: export with transparent background so we can composite manually
      const mobileOptions = isMobileDevice
        ? {
            ...baseOptions,
            backgroundColor: "transparent",
            style: { backgroundColor: "transparent" },
            filter: (node: unknown) => {
              const el = node as HTMLElement | null;
              if (!el || typeof (el as any).getAttribute !== "function") return true;
              return el.getAttribute("data-export-bg") !== "true";
            },
          }
        : baseOptions;

      // Retry logic for mobile
      let blob: Blob | null = null;
      const maxAttempts = isMobileDevice ? 3 : 1;
      
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
            await nextPaint();
            await waitForImages(element);
          }
          blob = await toBlob(element, mobileOptions as any);
          if (blob && blob.size > 1000) break; // Valid blob
        } catch {
          // retry
        }
      }

      // Fallback: if toBlob returns null, use PNG
      if (!blob) {
        const dataUrl = await toPng(element, mobileOptions as any);
        blob = dataUrlToPngBlobChunked(dataUrl);
      }

      if (!blob) throw new Error("Export failed: blob is null");

      // Mobile: composite foreground onto background via canvas to guarantee background shows
      if (isMobileDevice && blob) {
        try {
          const bgSrc = bgDataUrl || (await imageToDataUrl(shareCardBg));
          blob = await composePngOnBackground({
            width,
            height,
            backgroundSrc: bgSrc,
            foregroundBlob: blob,
          });
        } catch (e) {
          console.warn("[export] background compose failed, using foreground only:", e);
        }
      }

      const file = new File([blob], filename, { type: blob.type || "image/png" });

      // iOS Safari doesn't save blobs like desktop
      if (isIOSDevice) {
        const blobUrl = URL.createObjectURL(blob);

        let shareSupported = false;
        try {
          const navAny = navigator as any;
          shareSupported =
            typeof navAny.share === "function" &&
            (typeof navAny.canShare !== "function" || navAny.canShare({ files: [file] }) === true);
        } catch {
          shareSupported = false;
        }

        setIosSave({ filename, file, blobUrl, shareSupported });
        toast.success(
          shareSupported
            ? "Grafika gotowa — wybierz Udostępnij → Zapisz obraz"
            : "Grafika gotowa — otwórz obraz i przytrzymaj, aby zapisać"
        );
        return;
      }

      // Mobile (Android): try native share
      if (isMobileDevice && navigator.share) {
        try {
          const canShareFiles = (navigator as any).canShare ? (navigator as any).canShare({ files: [file] }) : true;
          if (canShareFiles) {
            await navigator.share({ files: [file], title: filename });
            toast.success(`Udostępniono ${filename}`);
            return;
          }
        } catch {
          // fallback below
        }
      }

      // Desktop/Android fallback: classic download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = filename;
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast.success(`Pobrano ${filename}`);
    } catch (error) {
      console.error("Error generating image:", error);
      toast.error("Błąd generowania obrazu");
    } finally {
      restoreRootBg?.();
      setDownloading(null);
    }
  };

  const downloadAll = async () => {
    if (isIOSDevice) {
      toast.error("Na iOS pobieranie wszystkich naraz nie działa — pobierz pojedyncze grafiki.");
      return;
    }
    setDownloadingAll(true);
    for (const g of visibleGraphics) {
      await downloadGraphic(g.id, `${g.id}.png`, g.width, g.height);
      await new Promise((r) => setTimeout(r, 500));
    }
    setDownloadingAll(false);
  };

  const allReady = !!bgDataUrl;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Instagram className="w-5 h-5 text-primary" />
          <h2 className="font-display font-semibold text-lg">Grafiki Instagram</h2>
          {hiddenIds.length > 0 && (
            <Badge variant="outline" className="ml-2">{hiddenIds.length} ukrytych</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hiddenIds.length > 0 && (
            <Button
              onClick={() => setShowHidden(!showHidden)}
              variant="ghost"
              size="sm"
              className="touch-manipulation"
            >
              {showHidden ? (
                <EyeOff className="w-4 h-4 mr-2" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              {showHidden ? "Schowaj ukryte" : "Pokaż ukryte"}
            </Button>
          )}
          <Button onClick={downloadAll} variant="outline" size="sm" disabled={downloadingAll || !allReady || visibleGraphics.length === 0}>
            {downloadingAll ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Pobierz wszystkie
          </Button>
        </div>
      </div>

      {/* Hidden graphics section */}
      {showHidden && hiddenGraphics.length > 0 && (
        <Card className="border-dashed">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-muted-foreground">Ukryte szablony</CardTitle>
              <Button onClick={restoreAll} variant="ghost" size="sm" className="text-xs">
                Przywróć wszystkie
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex flex-wrap gap-2">
              {hiddenGraphics.map((g) => (
                <Badge
                  key={g.id}
                  variant="secondary"
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors touch-manipulation py-2 px-3"
                  onClick={() => restoreGraphic(g.id)}
                >
                  {g.label}
                  <Eye className="w-3 h-3 ml-2" />
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {visibleGraphics.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <EyeOff className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="font-medium">Wszystkie szablony są ukryte</p>
            <Button onClick={restoreAll} variant="link" className="mt-2">
              Przywróć wszystkie
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {visibleGraphics.map((g) => (
            <Card key={g.id} className="overflow-hidden group">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center justify-between gap-2">
                  <span className="truncate">{g.label}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary" className="font-normal">{g.format}</Badge>
                    <Button
                      onClick={() => hideGraphic(g.id)}
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 sm:h-7 sm:w-7 text-muted-foreground hover:text-destructive touch-manipulation active:bg-destructive/10 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-5 w-5 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {/* Scaled preview container */}
                <div 
                  className="relative bg-muted rounded-lg overflow-hidden mb-3"
                  style={{
                    aspectRatio: `${g.width}/${g.height}`,
                  }}
                >
                  <div
                    className="absolute top-0 left-0 origin-top-left"
                    style={{
                      transform: `scale(${324 / g.width})`,
                      width: `${g.width}px`,
                      height: `${g.height}px`,
                    }}
                  >
                    <g.Component backgroundDataUrl={getBackgroundForGraphic(g.id)} />
                  </div>
                </div>
                <Button
                  onClick={() => downloadGraphic(g.id, `${g.id}.png`, g.width, g.height)}
                  disabled={downloading === g.id || !allReady}
                  className="w-full"
                  size="sm"
                >
                  {downloading === g.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Pobierz PNG
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* iOS save dialog */}
      <Dialog
        open={!!iosSave}
        onOpenChange={(open) => {
          if (!open) {
            if (iosSave?.blobUrl) URL.revokeObjectURL(iosSave.blobUrl);
            setIosSave(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Zapisz grafikę</DialogTitle>
            <DialogDescription>
              Na iPhone plik nie „pobiera się” jak na desktop. Najpewniej: przytrzymaj podgląd grafiki poniżej i wybierz „Zapisz w Zdjęciach”.
            </DialogDescription>
          </DialogHeader>

          {iosSave && (
            <div className="rounded-lg overflow-hidden border bg-transparent">
              <img
                src={iosSave.blobUrl}
                alt={`Podgląd ${iosSave.filename}`}
                className="w-full h-auto"
                loading="eager"
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (!iosSave) return;
                const opened = window.open(iosSave.blobUrl, "_blank");
                if (!opened) {
                  window.location.href = iosSave.blobUrl;
                }
              }}
            >
              Otwórz obraz (przytrzymaj → Zapisz)
            </Button>

            {iosSave?.shareSupported ? (
              <Button
                onClick={async () => {
                  if (!iosSave) return;
                  try {
                    await navigator.share({ files: [iosSave.file], title: iosSave.filename });
                  } catch {
                    // user cancelled
                  }
                }}
              >
                Udostępnij / Zapisz
              </Button>
            ) : (
              <div className="text-xs text-muted-foreground leading-snug text-right">
                Ta przeglądarka (podgląd) nie obsługuje zapisu plików.
                <br />
                Spróbuj: przytrzymaj podgląd powyżej / „Otwórz obraz” / „Kopiuj obraz”.
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export stage (renders only when exporting). Kept near viewport for iOS reliability. */}
      {exportingGraphic && (
        <div
          className="fixed pointer-events-none"
          style={{
            left: 0,
            top: 0,
            // IMPORTANT (iOS): keeping the stage *near* the viewport improves image decoding.
            // We hide it via opacity on the wrapper (not on the captured node), so the export
            // remains fully opaque.
            opacity: 0,
            width: `${exportingGraphic.width}px`,
            height: `${exportingGraphic.height}px`,
          }}
          aria-hidden="true"
        >
          <div
            ref={exportRef}
            style={{
              width: `${exportingGraphic.width}px`,
              height: `${exportingGraphic.height}px`,
            }}
          >
            <exportingGraphic.Component backgroundDataUrl={getBackgroundForGraphic(exportingGraphic.id)} />
          </div>
        </div>
      )}
    </div>
  );
};
